/**
 * Shared parse tokens for compliance document placeholders.
 * Used by client TipTap marks, server markdown counting, LLM output, and PDF export.
 */

export const NOT_FOUND_PLACEHOLDER = '[NOT_FOUND]'
export const MANUAL_INSTRUCTION_PLACEHOLDER = '[MANUAL_INSTRUCTION]'
export const INSUFFICIENT_INFORMATION_PLACEHOLDER = '[INSUFFICIENT_INFORMATION]'

/** Canonical missing-field token (replaces legacy NOT_SPECIFIED / NOT_FOUND_IN_DOCUMENTS). */
export const NOT_FOUND_REGEX = /\\?\[NOT\\?_FOUND(?::\s*(.+?))?\\?\]/g

/** Legacy tokens — still parsed for pre-consolidation markdown. */
export const LEGACY_NOT_SPECIFIED_REGEX =
  /\\?\[NOT\\?_SPECIFIED(?::\s*(.+?))?\\?\]/g
export const LEGACY_NOT_FOUND_IN_DOCUMENTS_REGEX =
  /\\?\[NOT\\?_FOUND\\?_IN\\?_DOCUMENTS(?::\s*(.+?))?\\?\]/g

/** Longest keyword first so NOT_FOUND_IN_DOCUMENTS does not partially match NOT_FOUND. */
export const MISSING_FIELD_PLACEHOLDER_REGEXES = [
  LEGACY_NOT_FOUND_IN_DOCUMENTS_REGEX,
  LEGACY_NOT_SPECIFIED_REGEX,
  NOT_FOUND_REGEX,
] as const

export const MANUAL_INSTRUCTION_REGEX =
  /\\?\[MANUAL\\?_INSTRUCTION(?::\s*(.+?))?\\?\]/g
export const INSUFFICIENT_INFORMATION_REGEX =
  /\\?\[INSUFFICIENT\\?_INFORMATION\\?\]/g

export const NOT_FOUND_PLACEHOLDER_COLON_PREFIX =
  `${NOT_FOUND_PLACEHOLDER.slice(0, -1)}:`
export const MANUAL_INSTRUCTION_PLACEHOLDER_COLON_PREFIX =
  `${MANUAL_INSTRUCTION_PLACEHOLDER.slice(0, -1)}:`

/** ALL_CAPS keywords; TipTap markdown may escape `_` as `\_` inside these spellings. */
const COMPLIANCE_DOCUMENT_SENTINEL_KEYWORDS = [
  'NOT_FOUND_IN_DOCUMENTS',
  'NOT_SPECIFIED',
  'NOT_FOUND',
  'MANUAL_INSTRUCTION',
  'INSUFFICIENT_INFORMATION',
] as const

export function notFoundWithField(fieldLabel: string): string {
  return `${NOT_FOUND_PLACEHOLDER_COLON_PREFIX} ${fieldLabel}]`
}

export function notFoundWithGuidance(
  fieldLabel: string,
  guidance: string,
): string {
  return `${NOT_FOUND_PLACEHOLDER_COLON_PREFIX} ${fieldLabel} | ${guidance}]`
}

export function manualInstructionWithGuidance(instruction: string): string {
  return `${MANUAL_INSTRUCTION_PLACEHOLDER_COLON_PREFIX} ${instruction}]`
}

function unescapeTipTapMarkdownUnderscoresInSentinelKeywords(
  markdown: string,
): string {
  let result = markdown
  for (const keyword of COMPLIANCE_DOCUMENT_SENTINEL_KEYWORDS) {
    const escapedKeyword = keyword.replace(/_/g, '\\_')
    result = result.split(escapedKeyword).join(keyword)
  }
  return result
}

/** Strip zero-width chars, normalize newlines, and unescape TipTap markdown sentinel keywords. */
export function normalizeMarkdownForSentinelParsing(markdown: string): string {
  return unescapeTipTapMarkdownUnderscoresInSentinelKeywords(
    markdown
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\r\n/g, '\n'),
  )
}

export function countNotFoundTokenOpeners(markdown: string): number {
  const normalized = normalizeMarkdownForSentinelParsing(markdown)
  let count = 0
  for (const regex of MISSING_FIELD_PLACEHOLDER_REGEXES) {
    regex.lastIndex = 0
    count += normalized.match(regex)?.length ?? 0
  }
  return count
}

/** @deprecated Use countNotFoundTokenOpeners */
export function countNotSpecifiedTokenOpeners(markdown: string): number {
  return countNotFoundTokenOpeners(markdown)
}

/**
 * Normalize the guidance segment after `|` for display and counting.
 * Treats empty/whitespace-only text and the accidental literal token "guidance" as absent.
 */
export function normalizePlaceholderGuidanceSegment(value: string): string | null {
  const normalized = value.trim()
  if (!normalized) {
    return null
  }
  if (normalized.toLowerCase() === 'guidance') {
    return null
  }
  return normalized
}

export function normalizePlaceholderGuidanceAttribute(
  raw: string | null | undefined,
): string | null {
  if (raw == null) {
    return null
  }
  return normalizePlaceholderGuidanceSegment(raw)
}

/**
 * Split the captured group after `: ` into field label and optional guidance (after `|`).
 */
export function parsePlaceholderColonContent(raw: string): {
  fieldLabel: string
  guidance: string | null
} {
  const pipeIdx = raw.indexOf('|')
  if (pipeIdx === -1) {
    return { fieldLabel: raw.trim(), guidance: null }
  }
  return {
    fieldLabel: raw.slice(0, pipeIdx).trim(),
    guidance: normalizePlaceholderGuidanceSegment(raw.slice(pipeIdx + 1)),
  }
}
